import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import CampaignForm from './CampaignForm'
import LineItemForm from './LineItemForm'
import AdFacebookCreateForm from '../../AdForms/AdFacebookCreate/AdFacebookCreateForm'

import { AdFacebookCreateContextProvider } from '../../formsContexts/AdFacebookCreateFormContextProvider'
import useContinuousForm from '../../../../hooks/formHooks/useContinuousForm'

import { getSelfAccount } from '../../../../modules/actions/selfAccounts'
import {
  facebookRightUploadAdCampaignSelector,
  facebookRightUploadAdLineItemSelector
} from '../../../../modules/selectors/rights'
import { selectedPartnershipRightsHolderSelfAccountIdSelector } from '../../../../modules/selectors/app'

import { ROUTE_PARAMS } from '../../../../constants/routes'
import { OUTCOME_APP_PROMOTION, OUTCOME_TRAFFIC } from '../../../../constants/campaigns'
import {
  ARE_WE_ABLE_TO_TRACK_LANDING_PAGE_VIEWS,
  NO_I_HAVE_NOT_LINKED_BY_WEBSITE_PIXEL,
  OBJECT_STORE_URL,
  YES_I_HAVE_LINKED_BY_WEBSITE_PIXEL
} from './LineItemForm/fields'

import { PROMOTED_PIXEL } from '../../../../constants/selectLists/customEventTypes/fields'
import { CALL_TO_ACTION, CLICK_THROUGH_LINK } from '../../AdForms/fields'
import { APPLICATION_ID } from './reusableFields/AppListField/fields'
import { LineItemFormContextProvider } from './LineItemForm/LineItemFormContextProvider'

export const CAMPAIGN_FORM = 'campaign_form'
export const LINE_ITEM_FORM = 'line_item_form'
export const AD_FORM = 'ad_form'

const UploadFacebookAdCreativeForm = ({ onSuccessSubmit }) => {
  const dispatch = useDispatch()

  // If user select Reach - in payload we send 'outcome_awareness' objective, that's why we need to store it in separate state
  const [selectedCampaignObjective, setSelectedCampaignObjective] = useState('')

  const campaign = useSelector(facebookRightUploadAdCampaignSelector)
  const lineItem = useSelector(facebookRightUploadAdLineItemSelector)
  const selectedPartnershipRightsHolderAccountId = useSelector(selectedPartnershipRightsHolderSelfAccountIdSelector)

  const { objective: campaignObjective } = campaign

  const [lineItemFormPromotedObjectData, setLineItemFormPromotedObjectData] = useState({
    ...(campaignObjective === OUTCOME_TRAFFIC && {
      // 1. For traffic objective campaigns with cost cap bidding type we handle pixel selection on lineItem form
      [ARE_WE_ABLE_TO_TRACK_LANDING_PAGE_VIEWS]: NO_I_HAVE_NOT_LINKED_BY_WEBSITE_PIXEL,
      [PROMOTED_PIXEL]: ''
    }),
    ...(campaignObjective === OUTCOME_APP_PROMOTION && {
      // 2. For outcome_app_promotion campaigns we selected application id and object store url in lineItem form and pass to ad form
      [APPLICATION_ID]: '',
      [OBJECT_STORE_URL]: ''
    })
  })

  const isLineItemHasData = !!Object.keys(lineItem).length

  const { activatedForm, setActivatedForm } = useContinuousForm({
    initialForm: CAMPAIGN_FORM
  })

  const onSuccessAdFormSubmit = useCallback(() => {
    onSuccessSubmit({
      [ROUTE_PARAMS.campaignId]: campaign.id,
      [ROUTE_PARAMS.lineItemId]: lineItem.id
    })
  }, [campaign.id, lineItem.id, onSuccessSubmit])

  const formProgressItems = useMemo(
    () => [
      {
        label: 'Campaign',
        isActive: activatedForm === CAMPAIGN_FORM,
        isSuccess: activatedForm === LINE_ITEM_FORM || activatedForm === AD_FORM
      },
      {
        label: 'Ad set',
        isActive: activatedForm === LINE_ITEM_FORM,
        isSuccess: activatedForm === AD_FORM
      },
      {
        label: 'Creative assets',
        isActive: activatedForm === AD_FORM,
        isSuccess: false
      }
    ],
    [activatedForm]
  )

  const additionalAdProps = useMemo(() => {
    if (campaignObjective === OUTCOME_APP_PROMOTION) {
      return {
        // on digital right page we handle application selection on lineItem form and pass it to tracking_specs instead of the pixel
        selectedApplicationId: lineItemFormPromotedObjectData[APPLICATION_ID],
        // pixel is used on a website, but in case we are linking to the app store for outcome_app_promotion
        allowSendPixel: false
      }
    } else if (campaignObjective === OUTCOME_TRAFFIC) {
      return {
        // If campaign objective is traffic - we handle pixel selection on lineItem form and need pass it to ad form
        selectedPixelPassedFromOutside: lineItemFormPromotedObjectData[PROMOTED_PIXEL],
        // for traffic campaign with bidding type cost_cap we handle pixel selection on lineItem form and pass it to ad form
        allowSendPixel:
          lineItemFormPromotedObjectData[ARE_WE_ABLE_TO_TRACK_LANDING_PAGE_VIEWS] === YES_I_HAVE_LINKED_BY_WEBSITE_PIXEL
      }
    }
  }, [campaignObjective, lineItemFormPromotedObjectData])

  const getActivatedForm = useCallback(() => {
    switch (activatedForm) {
      case LINE_ITEM_FORM:
        return (
          <LineItemFormContextProvider>
            <LineItemForm
              formProgressItems={formProgressItems}
              selectedCampaignObjective={selectedCampaignObjective}
              onSuccessSubmit={promotedObjectData => {
                // There are cases when we use data selected in lineItem form in ad form
                // 1. For traffic objective campaigns with cost cap bidding type we handle pixel selection on lineItem form
                // and pass it to the ad form
                // 2. For outcome_app_promotion campaigns we need to pass selected application id to ad form
                if (promotedObjectData) {
                  setLineItemFormPromotedObjectData(promotedObjectData)
                }
                setActivatedForm(AD_FORM)
              }}
            />
          </LineItemFormContextProvider>
        )
      case AD_FORM:
        return (
          <AdFacebookCreateContextProvider
            preselectedValues={{
              // todo move other props which are used only on UploadFacebookAdCreativeForm to context
              ...(campaignObjective === OUTCOME_APP_PROMOTION && {
                [CLICK_THROUGH_LINK]: lineItemFormPromotedObjectData[OBJECT_STORE_URL],
                [CALL_TO_ACTION]: 'INSTALL_MOBILE_APP'
              }),
              ...(lineItem.objective === 'page_likes' &&
                lineItem.promoted_object?.page_id && {
                  [CLICK_THROUGH_LINK]: 'https://www.facebook.com/' + lineItem.promoted_object.page_id,
                  [CALL_TO_ACTION]: 'LIKE_PAGE'
                })
            }}
            campaignObjective={campaignObjective}
            lineItem={lineItem}
          >
            <AdFacebookCreateForm
              formProgressItems={formProgressItems}
              campaignObjective={campaignObjective}
              lineItemWasLoaded={isLineItemHasData}
              onSuccessSubmit={onSuccessAdFormSubmit}
              {...additionalAdProps}
            />
          </AdFacebookCreateContextProvider>
        )
      case CAMPAIGN_FORM:
      default:
        return (
          <CampaignForm
            formProgressItems={formProgressItems}
            onSuccessSubmit={campaignObjective => {
              setSelectedCampaignObjective(campaignObjective)
              setActivatedForm(LINE_ITEM_FORM)

              // the selfAccount is required for Line item form for outcome_app_promotion campaigns
              if (campaignObjective === OUTCOME_APP_PROMOTION && selectedPartnershipRightsHolderAccountId) {
                dispatch(getSelfAccount(selectedPartnershipRightsHolderAccountId))
              }
            }}
          />
        )
    }
  }, [
    dispatch,
    selectedPartnershipRightsHolderAccountId,
    activatedForm,
    campaignObjective,
    isLineItemHasData,
    onSuccessAdFormSubmit,
    formProgressItems,
    setActivatedForm,
    selectedCampaignObjective,
    additionalAdProps,
    lineItemFormPromotedObjectData,
    lineItem
  ])

  return getActivatedForm()
}

export default UploadFacebookAdCreativeForm
